import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  genders,
  associateSchools,
  academicStatuses,
  professionalStatuses,
  getYearOptions,
} from "../../../utils/options";
import "./RejectedPage.css";
import { Printer, FileSpreadsheet } from "lucide-react";
import * as XLSX from "xlsx";
import { printTableWithHeader } from "../../../utils/printUtils";

const PAGE_SIZE = 30;
const API_BASE_URL = process.env.REACT_APP_BACKEND_API_URL;

const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" });
};

const formatTime = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
};

export default function RejectedPage() {
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({
    gender: "",
    year: "",
    associateSchool: "",
    academicStatus: "",
    professionalStatus: "",
  });
  const [rejectedAlumni, setRejectedAlumni] = useState([]);
  const [serverTime, setServerTime] = useState({ serverDate: "", serverTime: "", fullDateTime: "" });
  const [currentUser, setCurrentUser] = useState({ fullname: "", systemRole: "" });

  // Load current user
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user) setCurrentUser({ fullname: user.fullname, systemRole: user.systemRole });
  }, []);

  // Fetch rejected alumni data
  const fetchRejectedAlumni = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/registrations/rejected`);
      if (res.data.success) setRejectedAlumni(res.data.data);
    } catch (err) {
      console.error("Failed to fetch rejected alumni:", err.message);
    }
  };

  // Fetch server time
  const fetchServerTime = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/time/datetime`);
      if (res.data.success) setServerTime(res.data);
    } catch (err) {
      console.error("Failed to fetch server time:", err.message);
    }
  };

  useEffect(() => {
    fetchRejectedAlumni();
    fetchServerTime();
    const timer = setTimeout(() => setLoading(false), 1200);
    return () => clearTimeout(timer);
  }, []);

  // Filtered and searched data
  const filteredAlumni = useMemo(() => {
    return rejectedAlumni.filter((a) => {
      const fullName = [a.title, a.firstName, a.middleName, a.lastName].filter(Boolean).join(" ");
      const matchesSearch =
        a.alumniID.toLowerCase().includes(search.toLowerCase()) ||
        a.email.toLowerCase().includes(search.toLowerCase()) ||
        fullName.toLowerCase().includes(search.toLowerCase());

      const matchesFilters =
        (!filters.gender || a.gender === filters.gender) &&
        (!filters.year || a.yearOfGraduation === Number(filters.year)) &&
        (!filters.associateSchool || a.associateSchool === filters.associateSchool) &&
        (!filters.academicStatus || a.academicStatus === filters.academicStatus) &&
        (!filters.professionalStatus || a.professionalStatus === filters.professionalStatus);

      return matchesSearch && matchesFilters;
    });
  }, [search, filters, rejectedAlumni]);

  const totalPages = Math.ceil(filteredAlumni.length / PAGE_SIZE);
  const paginatedData = filteredAlumni.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Handle status update (optional if re-approve is needed)
  const handleStatusUpdate = async (alumniId, newStatus) => {
    if (!window.confirm(`Are you sure you want to mark this registration as ${newStatus}?`)) return;

    try {
      await axios.put(`${API_BASE_URL}/registrations/${alumniId}/status`, {
        registrationStatus: newStatus,
        approvedBy: currentUser.fullname,
      });
      fetchRejectedAlumni();
      alert(`Alumni registration has been ${newStatus} successfully.`);
    } catch (err) {
      console.error("Failed to update registration status:", err.message);
      alert("Failed to update registration status.");
    }
  };

  // ===============================
  // Print functionality
  // ===============================
  const handlePrint = () => {
    printTableWithHeader({
      title: "Rejected Registrations List",
      summary: "List of all Rejected Registrations",
      columns: [
        { key: "sn", label: "SN" },
        { key: "alumniID", label: "Alumni ID" },
        { key: "fullName", label: "Full Name" },
        { key: "gender", label: "Gender" },
        { key: "phone", label: "Telephone" },
        { key: "status", label: "Status" },
      ],
      rows: filteredAlumni.map((a, idx) => ({
        sn: idx + 1,
        alumniID: a.alumniID,
        fullName: [a.title, a.firstName, a.middleName, a.lastName].filter(Boolean).join(" "),
        gender: a.gender,
        phone: a.mobileNumbers?.[0] ? `+${a.mobileNumbers[0]}` : "",
        status: a.registrationStatus,
      })),
    });
  };

  // ===============================
  // Export to Excel functionality
  // ===============================
  const handleExportExcel = () => {
    const dataForExcel = filteredAlumni.map((a, idx) => ({
      SN: idx + 1,
      "Alumni ID": a.alumniID,
      "Full Name": [a.title, a.firstName, a.middleName, a.lastName].filter(Boolean).join(" "),
      Gender: a.gender,
      Telephone: a.mobileNumbers?.[0] ? `+${a.mobileNumbers[0]}` : "",
      Status: a.registrationStatus,
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataForExcel);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "RejectedAlumni");
    XLSX.writeFile(workbook, "rejected_alumni.xlsx");
  };

  return (
    <div className="rejected-alumni-page">
      <div className="rejected-user-card">
        <div className="rejected-user-left">
          <div className="rejected-user-name">Welcome, {currentUser.fullname}</div>
          <div className="rejected-user-role">System Role: {currentUser.systemRole}</div>
        </div>
        <div className="rejected-user-right">
          <div className="rejected-user-date">{formatDate(serverTime.fullDateTime)}</div>
          <div className="rejected-user-time">{formatTime(serverTime.fullDateTime)}</div>
        </div>
      </div>

      <h1 className="rejected-page-title">Rejected Alumni Registrations</h1>

      <div className="rejected-action-row">
        <button className="rejected-btn-primary" onClick={handlePrint}><Printer size={16} /> Print</button>
        <button className="rejected-btn-secondary" onClick={handleExportExcel}><FileSpreadsheet size={16} /> Export</button>
      </div>

      <div className="rejected-filters">
        <select onChange={(e) => setFilters({ ...filters, gender: e.target.value })}>
          <option value="">Gender</option>
          {genders.map((g) => <option key={g} value={g}>{g}</option>)}
        </select>

        <select onChange={(e) => setFilters({ ...filters, year: e.target.value })}>
          <option value="">Year of Graduation</option>
          {getYearOptions(50).map((y) => <option key={y} value={y}>{y}</option>)}
        </select>

        <select onChange={(e) => setFilters({ ...filters, associateSchool: e.target.value })}>
          <option value="">Associate School</option>
          {associateSchools.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>

        <select onChange={(e) => setFilters({ ...filters, academicStatus: e.target.value })}>
          <option value="">Academic Status</option>
          {academicStatuses.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>

        <select onChange={(e) => setFilters({ ...filters, professionalStatus: e.target.value })}>
          <option value="">Professional Status</option>
          {professionalStatuses.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <input
        className="rejected-search-input"
        placeholder="Search by Alumni ID, Full Name or Email"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      <div className="rejected-table-card">
        <div className="rejected-table-meta">
          <span>Total Rejected: {filteredAlumni.length}</span>
          <span>Page {page} of {totalPages}</span>
        </div>

        {loading ? (
          <div className="rejected-loading">Loading rejected alumni…</div>
        ) : (
          <table className="rejected-table">
            <thead>
              <tr>
                <th>SN</th>
                <th>Alumni ID</th>
                <th>Full Name</th>
                <th>Gender</th>
                <th>Telephone</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.map((a, idx) => {
                const fullName = [a.title, a.firstName, a.middleName, a.lastName].filter(Boolean).join(" ");
                return (
                  <tr key={a._id}>
                    <td>{(page - 1) * PAGE_SIZE + idx + 1}</td>
                    <td>{a.alumniID}</td>
                    <td>{fullName}</td>
                    <td>{a.gender}</td>
                    <td>{a.mobileNumbers?.[0] ? `+${a.mobileNumbers[0]}` : ""}</td>
                    <td className="rejected-approve-cell">
                      <select
                        value={a.registrationStatus}
                        onChange={(e) => handleStatusUpdate(a._id, e.target.value)}
                      >
                        <option value="REJECTED">REJECTED</option>
                        <option value="PENDING">PENDING</option>
                        <option value="APPROVED">APPROVED</option>
                      </select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        <div className="rejected-pagination">
          <button disabled={page === 1} onClick={() => setPage(page - 1)}>Previous</button>
          <button disabled={page === totalPages} onClick={() => setPage(page + 1)}>Next</button>
        </div>
      </div>
    </div>
  );
}